import React, { useState } from "react";
import { useAppSelector } from "../../../redux/store";
import { useUser } from "../auth/is_loggedin";
import MenuFormPage from "../menu-page/menu-form-page";
import { useTranslation } from "react-i18next";
import useGameSettings, { ModeFlags, getRoomModeFromFlags } from "../../../hooks/game-settings/useGameSettings";
import useGameCreation from "../../../hooks/game-settings/useGameCreation";
import BasicGameSettings from "./sections/BasicGameSettings";
import PromptSettings from "./sections/PromptSettings";
import PlayerSettings from "./sections/PlayerSettings";
import AdvancedSettings from "./sections/AdvancedSettings";
import AdvancedToggleButton from "./sections/AdvancedToggleButton";
import ErrorDisplay from "./sections/ErrorDisplay";
import { FeatureGate } from "../../common/feature-gate/FeatureGate";

type Props = {
  modeFlags: ModeFlags;
};

const GameSettingsComponent = ({ modeFlags }: Props) => {
  const { t } = useTranslation(["game-settings", "common"]);
  const selectPlayerDetails = useAppSelector(
    (state) => state.menu.playerDetails
  );
  const user = useUser();

  // Consolidated state management
  const {
    settings,
    updateBasic,
    updatePrompts,
    updatePlayers,
    updateAdvanced,
    getAllSettings,
  } = useGameSettings(modeFlags, selectPlayerDetails);

  // Simplified submission handling
  const { createGame, isSubmitting, error, clearError } = useGameCreation();

  // UI-only state
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleSubmit = async () => {
    if (!selectPlayerDetails) {
      alert("WHERE IS YOUR PLAYER?"); // Preserve exact original alert
      return;
    }

    const gameSettings = getAllSettings();
    const roomMode = getRoomModeFromFlags(modeFlags);
    await createGame(gameSettings, selectPlayerDetails, roomMode);
  };

  return (
    <MenuFormPage
      submit={handleSubmit}
      titles={[t("headers.default")]}
      subHeaders={[t("subheaders.default")]}
      submitText={t("footers.createGame")}
      ready={!isSubmitting}
    >
      <div className="flex-1 p-4 flex flex-col justify-center items-center space-y-2">
        <BasicGameSettings
          settings={settings.basic}
          onChange={updateBasic}
          modeFlags={modeFlags}
          canUseMature={selectPlayerDetails?.meta.mature ?? false}
        />

        <PromptSettings
          settings={settings.prompts}
          onChange={updatePrompts}
          maturity={settings.basic.maturity}
          userPremium={user?.premium ?? false}
          modeFlags={modeFlags}
        />

        {!showAdvanced && (
          <AdvancedToggleButton
            onClick={() => setShowAdvanced(true)}
            label={t("body.showAdvanced")}
          />
        )}

        {showAdvanced && (
          <>
            <AdvancedToggleButton
              onClick={() => setShowAdvanced(false)}
              label={t("body.hideAdvanced")}
              isHide
            />

            <PlayerSettings
              settings={settings.players}
              onChange={updatePlayers}
              modeFlags={modeFlags}
              maturity={settings.basic.maturity}
            />

            <AdvancedSettings
              settings={settings.advanced}
              onChange={updateAdvanced}
              modeFlags={modeFlags}
            />
          </>
        )}

        {error && <ErrorDisplay error={error} onDismiss={clearError} />}
      </div>
    </MenuFormPage>
  );
};

export default GameSettingsComponent;
